/**
 * Which Sunbrella (size, fabric-code) pairs March actually carries.
 *
 * CoverBase rows are generated as a size x fabric-code grid, which assumes
 * every size is offered in every colour. It is not: C604 and C608 carry
 * only 10 colours, C908/C758/C118 carry 48. The grid therefore produces
 * ~45 rows per short size for products that never existed, and any
 * size-level total taken across all of them is not comparable to the plan.
 *
 * This is the carried set, taken from the plan's Sunbrella tab (SWV/DWV
 * collapsed, since one CoverBase key covers the pair). Restricted to it,
 * C608 matches the plan exactly in FY2024 and FY2026.
 *
 * Demand DOES land on non-carried pairs — 306 units on C604 in FY2026 —
 * and it is real special-order work, not an attribution error, so those
 * keys are deliberately still tracked rather than deleted. The Demand
 * table separates them instead of dropping them.
 *
 * Not included: C1188, C852PD and C852CLAS. They are carried and on the
 * plan's tab, but their SKUs do not follow the size-code shape (e.g.
 * C852PD-14051-0054-57003-AL-BND503016), so no CoverBase key represents
 * them at all.
 */
export const SUNBRELLA_CARRIED: ReadonlySet<string> = new Set([
  "C118-48020",
  "C118-48022",
  "C118-48032",
  "C118-48080",
  "C118-48090",
  "C118-48095",
  "C118-5401",
  "C118-5402",
  "C118-5403",
  "C118-5404",
  "C118-54048",
  "C118-5405",
  "C118-5406",
  "C118-5407",
  "C118-5408",
  "C118-5410",
  "C118-5413",
  "C118-5414",
  "C118-5415",
  "C118-5416",
  "C118-5417",
  "C118-5422",
  "C118-5425",
  "C118-5429",
  "C118-5432",
  "C118-5439",
  "C118-5440",
  "C118-5446",
  "C118-5453",
  "C118-5457",
  "C118-5461",
  "C118-5462",
  "C118-5476",
  "C118-5488",
  "C118-5493",
  "C118-5499",
  "C118-56000",
  "C118-56001",
  "C118-5608",
  "C118-56095",
  "C118-56096",
  "C118-56105",
  "C118-57002",
  "C118-58029",
  "C118-58030",
  "C118-58038",
  "C118-58039",
  "C118-8318",
  "C604-5401",
  "C604-5403",
  "C604-5404",
  "C604-5408",
  "C604-5417",
  "C604-5422",
  "C604-5429",
  "C604-5439",
  "C604-5446",
  "C604-5457",
  "C608-5401",
  "C608-5403",
  "C608-5404",
  "C608-5408",
  "C608-5417",
  "C608-5422",
  "C608-5429",
  "C608-5439",
  "C608-5446",
  "C608-5457",
  "C758-48020",
  "C758-48022",
  "C758-48032",
  "C758-48080",
  "C758-48090",
  "C758-48095",
  "C758-5401",
  "C758-5402",
  "C758-5403",
  "C758-5404",
  "C758-54048",
  "C758-5405",
  "C758-5406",
  "C758-5407",
  "C758-5408",
  "C758-5410",
  "C758-5413",
  "C758-5414",
  "C758-5415",
  "C758-5416",
  "C758-5417",
  "C758-5422",
  "C758-5425",
  "C758-5429",
  "C758-5432",
  "C758-5439",
  "C758-5440",
  "C758-5446",
  "C758-5453",
  "C758-5457",
  "C758-5461",
  "C758-5462",
  "C758-5476",
  "C758-5488",
  "C758-5493",
  "C758-5499",
  "C758-56000",
  "C758-56001",
  "C758-5608",
  "C758-56095",
  "C758-56096",
  "C758-56105",
  "C758-57002",
  "C758-58029",
  "C758-58030",
  "C758-58038",
  "C758-58039",
  "C758-8318",
  "C908-48020",
  "C908-48022",
  "C908-48032",
  "C908-48080",
  "C908-48090",
  "C908-48095",
  "C908-5401",
  "C908-5402",
  "C908-5403",
  "C908-5404",
  "C908-54048",
  "C908-5405",
  "C908-5406",
  "C908-5407",
  "C908-5408",
  "C908-5410",
  "C908-5413",
  "C908-5414",
  "C908-5415",
  "C908-5416",
  "C908-5417",
  "C908-5422",
  "C908-5425",
  "C908-5429",
  "C908-5432",
  "C908-5439",
  "C908-5440",
  "C908-5446",
  "C908-5453",
  "C908-5457",
  "C908-5461",
  "C908-5462",
  "C908-5476",
  "C908-5488",
  "C908-5493",
  "C908-5499",
  "C908-56000",
  "C908-56001",
  "C908-5608",
  "C908-56095",
  "C908-56096",
  "C908-56105",
  "C908-57002",
  "C908-58029",
  "C908-58030",
  "C908-58038",
  "C908-58039",
  "C908-8318",
]);

export function isCarriedSunbrella(itemid: string): boolean {
  return SUNBRELLA_CARRIED.has(itemid);
}
